import { useState, useRef, useEffect } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import { createUserGoogle, setToken, requestOTP, verifyOTP } from '@genfun/core/api/auth';
import './LoginPopup.css';

function useLockBodyScroll(isOpen) {
    useEffect(() => {
        if (!isOpen) return;
        const html = document.documentElement;
        const body = document.body;
        const scrollbarWidth = window.innerWidth - html.clientWidth;
        const prev = {
            bodyOverflow: body.style.overflow,
            htmlOverflow: html.style.overflow,
            bodyPaddingRight: body.style.paddingRight,
        };
        body.style.overflow = 'hidden';
        html.style.overflow = 'hidden';
        if (scrollbarWidth > 0) body.style.paddingRight = `${scrollbarWidth}px`;
        return () => {
            body.style.overflow = prev.bodyOverflow;
            html.style.overflow = prev.htmlOverflow;
            body.style.paddingRight = prev.bodyPaddingRight;
        };
    }, [isOpen]);
}

function GoogleButton({ setSignedIn, setIsOpen, setError }) {
    const ref = localStorage.getItem('referral_code');
    const login = useGoogleLogin({
        onSuccess: async (creds) => {
            const resp = await createUserGoogle(creds, ref);
            if (resp.access_token === undefined && resp.detail !== undefined) {
                setError(resp.detail);
            } else {
                setToken(resp.access_token);
                setSignedIn(true);
                setIsOpen(false);
            }
        },
    });

    return (
        <button onClick={() => login()} className="artly-login-google-btn">
            <svg width="18" height="18" viewBox="0 0 18 18"><path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615Z" fill="#4285F4"/><path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18Z" fill="#34A853"/><path d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.997 8.997 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332Z" fill="#FBBC05"/><path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.163 6.656 3.58 9 3.58Z" fill="#EA4335"/></svg>
            <span>Continue with Google</span>
        </button>
    );
}

function EmailForm({ setSignedIn, setIsOpen }) {
    const emailRef = useRef('');
    const codeRef = useRef('');
    const [error, setError] = useState(null);
    const [awaitingCode, setAwaitingCode] = useState(false);
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);

    const ref = localStorage.getItem('referral_code');

    const handleRequestOTP = async () => {
        const val = emailRef.current;
        if (!val || val.trim() === '') {
            setError('Please enter your email');
            return;
        }
        setLoading(true);
        try {
            const [hasError, status] = await requestOTP(val, ref);
            if (hasError) {
                setError(status === 404 ? 'User not found' : status === 400 ? 'User is registered through Google' : 'Failed to send code');
            } else {
                setError(null);
                setEmail(val);
                setAwaitingCode(true);
            }
        } catch {
            setError('An error occurred');
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOTP = async () => {
        const val = codeRef.current;
        if (!val || val.trim() === '') {
            setError('Please enter the code');
            return;
        }
        setLoading(true);
        try {
            const [response, status] = await verifyOTP(email, val);
            if (status === 200) {
                setSignedIn(true);
                setIsOpen(false);
            } else {
                setError(response?.detail || 'Invalid code');
            }
        } catch {
            setError('An error occurred');
        } finally {
            setLoading(false);
        }
    };

    if (awaitingCode) {
        return (
            <div className="artly-login-form">
                <div className="artly-login-back" onClick={() => setAwaitingCode(false)}>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Back
                </div>
                <div className="artly-login-code-info">
                    Code sent to <strong>{email}</strong>
                </div>
                <input
                    type="text"
                    className="artly-login-input"
                    placeholder="Enter code"
                    onChange={(e) => { codeRef.current = e.target.value; }}
                />
                {error && <div className="artly-login-error">{error}</div>}
                <button className="artly-login-submit" onClick={handleVerifyOTP} disabled={loading}>
                    {loading ? 'Verifying...' : 'Log in'}
                </button>
                <div className="artly-login-resend" onClick={() => !loading && handleRequestOTP()}>
                    Resend code
                </div>
            </div>
        );
    }

    return (
        <div className="artly-login-form">
            <input
                type="email"
                className="artly-login-input"
                placeholder="Enter your email"
                onChange={(e) => { emailRef.current = e.target.value; }}
            />
            {error && <div className="artly-login-error">{error}</div>}
            <button className="artly-login-submit" onClick={handleRequestOTP} disabled={loading}>
                {loading ? 'Sending...' : 'Continue with email'}
            </button>
        </div>
    );
}

export function LoginPopup({ isOpen, setIsOpen, setSignedIn }) {
    const [error, setError] = useState(null);
    useLockBodyScroll(isOpen);

    if (!isOpen) return null;

    return (
        <div className="artly-login-overlay" onClick={() => setIsOpen(false)}>
            <div className="artly-login-popup" onClick={(e) => e.stopPropagation()}>
                <button className="artly-login-close" onClick={() => setIsOpen(false)}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>

                <div className="artly-login-header">
                    <h2>Welcome to Pornoclaw</h2>
                    <p>Sign in to start generating</p>
                </div>

                <GoogleButton setSignedIn={setSignedIn} setIsOpen={setIsOpen} setError={setError} />

                <div className="artly-login-divider">
                    <span>or</span>
                </div>

                <EmailForm setSignedIn={setSignedIn} setIsOpen={setIsOpen} />

                {error && <div className="artly-login-error">{error}</div>}
            </div>
        </div>
    );
}
